import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";

function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [files, setFiles] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    image_url: "",
    link_url: "",
    sort_order: 1,
    is_active: true,
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const previews = useMemo(
    () => files.map((file) => ({ name: file.name, url: URL.createObjectURL(file) })),
    [files]
  );

  useEffect(() => () => previews.forEach((item) => URL.revokeObjectURL(item.url)), [previews]);

  const loadBanners = async () => {
    const res = await API.get("/admin/banners");
    setBanners(Array.isArray(res.data) ? res.data : []);
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveBanner = async () => {
    if (!files.length && !form.image_url) {
      alert("Upload image or enter banner image URL");
      return;
    }

    const payload = new FormData();
    payload.append("title", form.title);
    payload.append("image_url", form.image_url);
    payload.append("link_url", form.link_url);
    payload.append("sort_order", form.sort_order);
    payload.append("is_active", String(form.is_active));
    files.forEach((file) => payload.append("images", file));

    await API.post("/admin/banners", payload, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    setForm({ title: "", image_url: "", link_url: "", sort_order: 1, is_active: true });
    setFiles([]);
    loadBanners();
  };

  const toggleBanner = async (banner) => {
    await API.put(`/admin/banners/${banner.id}`, {
      ...banner,
      is_active: !banner.is_active,
    });
    loadBanners();
  };

  const deleteBanner = async (id) => {
    await API.delete(`/admin/banners/${id}`);
    loadBanners();
  };

  const updateBannerField = (id, field, value) => {
    setBanners((current) =>
      current.map((banner) => (banner.id === id ? { ...banner, [field]: value } : banner))
    );
  };

  const saveBannerSettings = async (banner) => {
    setSavingId(banner.id);
    try {
      await API.put(`/admin/banners/${banner.id}`, {
        ...banner,
        sort_order: Number(banner.sort_order || 0),
        is_active: !!banner.is_active,
      });
      await loadBanners();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div style={{ borderLeft: "4px solid #4f46e5", paddingLeft: "14px" }}>
            <h2 style={title}>Banner Manager</h2>
            <p style={subtitle}>Upload banners and set Banner No. 1, 2, 3 to control the slider order.</p>
          </div>
        </div>

        <div style={formCard}>
          <div style={formGrid}>
            <div style={formGroup}>
              <label style={formLabel}>Banner Title</label>
              <input style={input} placeholder="e.g. Summer Promo" value={form.title} onChange={(event) => updateField("title", event.target.value)} />
            </div>
            <div style={formGroup}>
              <label style={formLabel}>Image URL (fallback)</label>
              <input style={input} placeholder="https://..." value={form.image_url} onChange={(event) => updateField("image_url", event.target.value)} />
            </div>
            <div style={formGroup}>
              <label style={formLabel}>Link URL (optional)</label>
              <input style={input} placeholder="https://..." value={form.link_url} onChange={(event) => updateField("link_url", event.target.value)} />
            </div>
            <div style={formGroup}>
              <label style={formLabel}>Banner Order No.</label>
              <input style={input} type="number" min="1" placeholder="1" value={form.sort_order} onChange={(event) => updateField("sort_order", Number(event.target.value))} />
            </div>
            <div style={formGroup}>
              <label style={formLabel}>Upload Image</label>
              <input style={fileInput} type="file" accept="image/*" multiple onChange={(event) => setFiles(Array.from(event.target.files || []))} />
            </div>
            <div style={formGroupEnd}>
              <label style={checkLabel}>
                <input type="checkbox" checked={form.is_active} onChange={(event) => updateField("is_active", event.target.checked)} />
                <span style={{ fontWeight: 700, color: "#334155" }}>Active</span>
              </label>
              <button type="button" style={primaryButton} onClick={saveBanner}>Add Banner</button>
            </div>
          </div>
        </div>

        {previews.length > 0 && (
          <div style={previewGrid}>
            {previews.map((item) => (
              <article key={item.name} style={bannerCard}>
                <img src={item.url} alt={item.name} style={bannerImage} />
                <div style={bannerInfo}>
                  <strong style={{ color: "#1e1b4b", fontSize: 14, fontWeight: 800 }}>{item.name}</strong>
                  <span style={previewBadge}>Preview</span>
                </div>
              </article>
            ))}
          </div>
        )}

        <div style={grid}>
          {banners.map((banner) => (
            <article key={banner.id} style={bannerCard}>
              <div style={bannerImageWrap}>
                <img src={getMediaUrl(banner.image_url)} alt={banner.title} style={bannerImage} />
                <span style={{ ...statusBadge, background: banner.is_active ? "#16a34a" : "#94a3b8" }}>
                  {banner.is_active ? "Active" : "Hidden"}
                </span>
              </div>
              <div style={bannerInfo}>
                <strong style={{ color: "#1e1b4b", fontSize: 15, fontWeight: 900 }}>{banner.title}</strong>
                <label style={numberLabel}>
                  <span>Banner Order No.</span>
                  <input
                    style={smallNumberInput}
                    type="number"
                    min="1"
                    value={banner.sort_order || 0}
                    onChange={(event) => updateBannerField(banner.id, "sort_order", Number(event.target.value))}
                  />
                </label>
              </div>
              <div style={actions}>
                <button type="button" style={saveButton} onClick={() => saveBannerSettings(banner)} disabled={savingId === banner.id}>
                  {savingId === banner.id ? "Saving..." : "Save Order"}
                </button>
                <button type="button" style={secondaryButton} onClick={() => toggleBanner(banner)}>
                  {banner.is_active ? "Hide" : "Show"}
                </button>
                <button type="button" style={dangerButton} onClick={() => deleteBanner(banner.id)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </AdminLayout>
  );
}

const page = { color: "#0f172a", display: "grid", gap: "20px" };
const header = { marginBottom: "4px" };
const title = { margin: 0, fontSize: 22, fontWeight: 900, color: "#1e1b4b" };
const subtitle = { margin: "4px 0 0", color: "#64748b", fontSize: 13 };
const formCard = { background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 16, padding: "20px 22px", boxShadow: "0 4px 20px rgba(79,70,229,0.08)" };
const formGrid = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px 1fr auto", gap: "14px", alignItems: "end" };
const formGroup = { display: "grid", gap: "8px" };
const formGroupEnd = { display: "grid", gap: "10px", alignItems: "center" };
const formLabel = { fontSize: 12, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em" };
const input = { height: "46px", border: "1.5px solid #e0e7ff", padding: "0 14px", borderRadius: "12px", fontSize: 14, outline: 0 };
const fileInput = { height: "46px", border: "1.5px solid #e0e7ff", padding: "8px 12px", borderRadius: "12px", fontSize: 13 };
const checkLabel = { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" };
const primaryButton = { height: "46px", padding: "0 20px", border: 0, background: "linear-gradient(135deg, #4f46e5, #312e81)", color: "#fff", borderRadius: "12px", fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(79,70,229,0.28)", whiteSpace: "nowrap" };
const previewGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" };
const bannerCard = { background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: "16px", overflow: "hidden", boxShadow: "0 4px 20px rgba(79,70,229,0.08)" };
const bannerImageWrap = { position: "relative" };
const bannerImage = { width: "100%", aspectRatio: "16 / 7", objectFit: "cover", display: "block" };
const statusBadge = { position: "absolute", top: 10, right: 10, color: "#fff", fontSize: 11, fontWeight: 900, padding: "4px 10px", borderRadius: 999, letterSpacing: ".04em" };
const previewBadge = { display: "inline-block", padding: "3px 10px", borderRadius: 999, background: "#ede9fe", color: "#4f46e5", fontSize: 11, fontWeight: 900 };
const bannerInfo = { padding: "14px 16px", display: "grid", gap: "10px" };
const numberLabel = { display: "grid", gap: "6px", color: "#475569", fontSize: "12px", fontWeight: 800 };
const smallNumberInput = { height: "42px", border: "1.5px solid #e0e7ff", borderRadius: "12px", padding: "0 14px", fontWeight: 800, outline: 0 };
const actions = { display: "flex", gap: "8px", padding: "0 16px 16px" };
const secondaryButton = { flex: 1, height: "38px", border: "1.5px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", borderRadius: "10px", cursor: "pointer", fontWeight: 700 };
const saveButton = { ...secondaryButton, borderColor: "#bbf7d0", background: "#ecfdf5", color: "#15803d", fontWeight: 800 };
const dangerButton = { ...secondaryButton, borderColor: "#fecaca", background: "#fef2f2", color: "#dc2626" };

export default AdminBanners;
